import { jsx as _jsx } from "react/jsx-runtime";
// src/plugins/builder/templates/auto-reply-positive/generator.tsx
// AI-powered thank-you replies for 4★ and 5★ reviews. Human-in-the-loop by default.
import meta from './template.json';
import { manifestFor, header, readme, assemble, safeString } from '../_helpers';
import { SimplePage } from '../../runtime/SimplePage';
const RuntimeUi = (ctx) => {
    const cfg = ctx.config;
    return (_jsx(SimplePage, { title: ctx.instanceName, subtitle: `Draft a ${cfg.tone} thank-you reply for every review with at least ${cfg.minStars}★.`, source: "reviews", filterReview: (r) => (r.rating ?? 0) >= (cfg.minStars ?? 4) && !r.response_body, actionLabel: "Write reply with AI", emptyTitle: "Nothing to thank right now", emptyBody: "New positive reviews will show up here automatically.", onAction: async (item, h) => {
            const r = item;
            const text = await h.ai(`Write a reply to this ${r.rating}-star review from ${r.author_name ?? 'a customer'} on ${r.platform}: "${r.body ?? ''}". Sign-off: ${cfg.signOff}`, `You write short, genuine thank-you replies. Voice: ${cfg.tone}. 2–3 sentences max.`);
            await h.reply(r.id, text);
            h.toast.success('Reply posted.');
        } }));
};
const runtimeHooks = {
    async onReviewCreated(ctx, review) {
        const r = review;
        const cfg = ctx.config;
        if (!cfg.autoPost)
            return;
        if (r.response_body)
            return;
        if ((r.rating ?? 0) < (cfg.minStars ?? 4))
            return;
        // Auto-post path happens in the hosted scheduled handler — UI-side auto-post is
        // already handled by SimplePage's action. Leaving the guard here so the hook
        // declares the behaviour even if the host invokes it headlessly.
    },
};
const generate = (params) => {
    const cfg = params.config;
    const manifest = manifestFor({
        instanceId: params.instanceId,
        name: params.instanceName,
        description: `Auto-reply to ${cfg.minStars}★+ reviews in a ${cfg.tone} voice.`,
        category: 'automation',
        icon: 'MessageSquareHeart',
        permissions: ['read:reviews', 'write:reviews', 'call:ai', 'ui:toast'],
        entry: 'both',
        hooks: { onReviewCreated: true },
        config: meta.configSchema,
        defaultConfig: { minStars: cfg.minStars, tone: cfg.tone, signOff: cfg.signOff, autoPost: cfg.autoPost },
    });
    const pluginTsx = header({ name: params.instanceName, templateId: meta.id, instanceId: params.instanceId }) +
        `import { useState } from 'react';
import { useReviews, useReplyToReview, useAI, useToast } from '../sdk';
import { Button } from '../../ui/Button';

export default function ${cls(params.instanceId)}() {
  const { reviews, refresh } = useReviews({ limit: 50 });
  const reply = useReplyToReview();
  const generate = useAI();
  const toast = useToast();
  const [busy, setBusy] = useState<Record<string, boolean>>({});

  const rows = (reviews ?? []).filter(r => (r.rating ?? 0) >= ${Number(cfg.minStars)} && !r.response_body);

  async function draftAndPost(id: string, body: string, stars: number) {
    setBusy(b => ({ ...b, [id]: true }));
    try {
      const r = await generate({
        system: 'You write short, genuine thank-you replies. Voice: ${esc(cfg.tone)}. 2-3 sentences.',
        messages: [{ role: 'user', content: \`Write a reply to this \${stars}-star review: "\${body}". Sign off with: ${esc(cfg.signOff)}\` }],
        max_tokens: 220,
        prompt_id: 'plugin:builder:${meta.id}',
      });
      await reply(id, r.text);
      toast.success('Reply posted.');
      await refresh();
    } catch (e: any) {
      toast.error(e?.message ?? 'Could not post the reply.');
    } finally {
      setBusy(b => ({ ...b, [id]: false }));
    }
  }

  return (
    <main id="main" className="mx-auto max-w-3xl px-6 py-10">
      <h1 className="text-2xl font-semibold">${esc(params.instanceName)}</h1>
      <p className="mt-1 text-sm text-slate-500">Thanking ${Number(cfg.minStars)}★+ reviews in a ${esc(cfg.tone)} voice.</p>
      <ul className="mt-6 space-y-3">
        {rows.map(r => (
          <li key={r.id} className="rounded-2xl bg-white p-4 ring-1 ring-slate-200">
            <p className="text-sm font-medium">{r.author_name ?? 'Anonymous'} · {r.rating}★</p>
            <p className="mt-1 text-sm text-slate-600">{r.body}</p>
            <div className="mt-3">
              <Button size="sm" disabled={!!busy[r.id]} onClick={() => draftAndPost(r.id, r.body ?? '', r.rating ?? 5)}>
                {busy[r.id] ? 'Working…' : 'Write reply with AI'}
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </main>
  );
}
`;
    const hooksTs = header({ name: params.instanceName, templateId: meta.id, instanceId: params.instanceId }) +
        `import type { PluginHandlers, HandlerContext } from '../types';
import { aiGenerate } from '../../ai/client';
import { supabase } from '../../supabase';

export const handlers: PluginHandlers = {
  async onReviewCreated(ctx: HandlerContext, review: unknown) {
    const r = review as { id: string; rating: number | null; body: string | null; response_body: string | null };
    if (!${cfg.autoPost}) return;
    if (r.response_body) return;
    if ((r.rating ?? 0) < ${Number(cfg.minStars)}) return;
    if (!ctx.permissions.includes('call:ai')) return;
    if (!ctx.permissions.includes('write:reviews')) return;
    const out = await aiGenerate({
      system: 'You write short thank-you replies. Voice: ${esc(cfg.tone)}.',
      messages: [{ role: 'user', content: \`Reply to this \${r.rating}-star review: "\${r.body ?? ''}"\` }],
      prompt_id: 'plugin:builder:${meta.id}',
      max_tokens: 220,
    });
    const text = out.text + '\\n\\n${esc(cfg.signOff)}';
    await supabase.from('reviews').update({ response_body: text }).eq('id', r.id);
  },
};
export default handlers;
`;
    return assemble({
        manifest,
        pluginTsx,
        hooksTs,
        readmeMd: readme({
            name: params.instanceName,
            templateId: meta.id,
            description: `AI-written thank-yous for ${cfg.minStars}★+ reviews. Tone: ${cfg.tone}.`,
            configSummary: [
                ['minStars', String(cfg.minStars)],
                ['tone', cfg.tone],
                ['signOff', cfg.signOff],
                ['autoPost', String(cfg.autoPost)],
            ],
        }),
    });
};
function esc(s) { return safeString(String(s)); }
function cls(id) { return 'P_' + id.replace(/[^a-zA-Z0-9]+/g, '_'); }
const template = {
    id: meta.id,
    name: meta.name,
    tagline: meta.tagline,
    icon: meta.icon,
    category: meta.category,
    permissions: meta.permissions,
    permissionSummary: meta.permissionSummary,
    entry: meta.entry,
    hooks: meta.hooks,
    configSchema: meta.configSchema,
    defaultConfig: meta.defaultConfig,
    suggestName: (cfg) => `Thank-yous for ${cfg.minStars}★+`,
    describe: (cfg) => {
        const c = cfg;
        return `For every review with ${c.minStars}★ or more, write a ${c.tone} thank-you with AI${c.autoPost ? ', then auto-post it' : ' for you to review before posting'}. Signs off with "${c.signOff}".`;
    },
    generate,
    runtimeUi: RuntimeUi,
    runtimeHooks,
    clickCount: meta.clickCount,
};
export default template;
